/**
 * Marque identity: a signature colour, a logo and a monogram for every make in the catalogue.
 *
 * The logo is the marque's own mark, shipped as a small WebP under `public/brands/<slug>.webp` and
 * shown on a white plate — the same nominative use every car marketplace makes of manufacturer
 * marks, and the only thing buyers actually scan a list of makes for. The colour drives the plate's
 * glow in the reveal; the monogram is the fallback for a make we have no artwork for, so a marque
 * added to the catalogue tomorrow still renders as a badge rather than a hole.
 *
 * Framework-agnostic on purpose — the table and the colour maths are plain TypeScript so a future
 * React Native shell can use exactly the same identities.
 */

export interface BrandIdentity {
  /** Hex, always `#rrggbb`. Drives the badge gradient and the reveal halo. */
  colour: string;
  /** One to three characters. Scaled down as it gets longer so the badge stays balanced. */
  monogram: string;
  /** Marques with no meaningful initial (the "other" bucket) get a car glyph instead. */
  glyph?: boolean;
  /** Absolute path to the marque's logo, when we ship one. */
  logo?: string;
}

/**
 * Colours are the shade each marque is popularly associated with, not sampled brand assets.
 * Where a marque is essentially monochrome the entry leans on a deep neutral rather than inventing
 * a colour it has never used.
 */
const IDENTITIES: Record<string, BrandIdentity> = {
  volkswagen: { colour: '#0a3d8f', monogram: 'VW' },
  opel: { colour: '#f7c600', monogram: 'O' },
  audi: { colour: '#b4121b', monogram: 'A' },
  bmw: { colour: '#0166b1', monogram: 'BMW' },
  'mercedes-benz': { colour: '#2b3a42', monogram: 'MB' },
  skoda: { colour: '#0e3a2e', monogram: 'Š' },
  renault: { colour: '#ffcc00', monogram: 'R' },
  peugeot: { colour: '#00263a', monogram: 'P' },
  citroen: { colour: '#c8102e', monogram: 'C' },
  ford: { colour: '#00327a', monogram: 'F' },
  fiat: { colour: '#8a1538', monogram: 'F' },
  toyota: { colour: '#eb0a1e', monogram: 'T' },
  hyundai: { colour: '#002c5f', monogram: 'H' },
  kia: { colour: '#05141f', monogram: 'KIA' },
  nissan: { colour: '#c3002f', monogram: 'N' },
  mazda: { colour: '#910a2d', monogram: 'M' },
  honda: { colour: '#cc0000', monogram: 'H' },
  volvo: { colour: '#1b3a6b', monogram: 'V' },
  seat: { colour: '#e1051e', monogram: 'S' },
  dacia: { colour: '#3e5641', monogram: 'D' },
  'alfa-romeo': { colour: '#981e32', monogram: 'AR' },
  chevrolet: { colour: '#c5a059', monogram: 'C' },
  mitsubishi: { colour: '#e60012', monogram: 'M' },
  suzuki: { colour: '#003da5', monogram: 'S' },
  subaru: { colour: '#013c74', monogram: 'S' },
  'land-rover': { colour: '#005a2b', monogram: 'LR' },
  jeep: { colour: '#12403c', monogram: 'J' },
  mini: { colour: '#1a1a1a', monogram: 'MI' },
  porsche: { colour: '#c9a227', monogram: 'P' },
  lexus: { colour: '#23272a', monogram: 'L' },
  smart: { colour: '#f5a623', monogram: 'S' },
  lancia: { colour: '#003d7c', monogram: 'L' },
  saab: { colour: '#004b87', monogram: 'S' },
  chrysler: { colour: '#002f6c', monogram: 'C' },
  dodge: { colour: '#b4131c', monogram: 'D' },
  jaguar: { colour: '#9f8b5c', monogram: 'J' },
  tesla: { colour: '#e31937', monogram: 'T' },
  cupra: { colour: '#9c6644', monogram: 'C' },
  ds: { colour: '#2e2a28', monogram: 'DS' },
  ssangyong: { colour: '#003876', monogram: 'SY' },
  lada: { colour: '#1b458f', monogram: 'L' },
  zastava: { colour: '#c8102e', monogram: 'Z' },
  daewoo: { colour: '#1f3c88', monogram: 'D' },
  daihatsu: { colour: '#e4002b', monogram: 'D' },
  rover: { colour: '#7b1e23', monogram: 'R' },
  mg: { colour: '#c8102e', monogram: 'MG' },
  infiniti: { colour: '#2a2e33', monogram: 'I' },
  cadillac: { colour: '#8a1538', monogram: 'C' },
  abarth: { colour: '#c8102e', monogram: 'A' },
  genesis: { colour: '#55524c', monogram: 'G' },
  maserati: { colour: '#003c71', monogram: 'M' },
  bentley: { colour: '#10312b', monogram: 'B' },
  ferrari: { colour: '#da291c', monogram: 'F' },
  lamborghini: { colour: '#ddb321', monogram: 'L' },
  'aston-martin': { colour: '#00665e', monogram: 'AM' },
  byd: { colour: '#0b2e5c', monogram: 'BYD' },
  chery: { colour: '#b71c1c', monogram: 'C' },
  'great-wall': { colour: '#1b3a6b', monogram: 'GW' },
  isuzu: { colour: '#c8102e', monogram: 'I' },
  iveco: { colour: '#003d7d', monogram: 'IV' },
  ostalo: { colour: '#5b6472', monogram: '', glyph: true },
};

/**
 * Marques we ship artwork for, as `public/brands/<slug>.webp`.
 *
 * A set rather than a field on `IDENTITIES` so the two can drift without breaking: a make can have
 * a colour and no logo (it falls back to the monogram) and the build never has to resolve an asset
 * that is not there.
 */
const LOGOS = new Set([
  'abarth', 'alfa-romeo', 'aston-martin', 'audi', 'bentley', 'bmw', 'byd', 'cadillac', 'chery',
  'chevrolet', 'chrysler', 'citroen', 'cupra', 'dacia', 'daewoo', 'daihatsu', 'dodge', 'ds',
  'ferrari', 'fiat', 'ford', 'genesis', 'great-wall', 'honda', 'hyundai', 'infiniti', 'isuzu',
  'iveco', 'jaguar', 'jeep', 'kia', 'lada', 'lamborghini', 'lancia', 'land-rover', 'lexus',
  'maserati', 'mazda', 'mercedes-benz', 'mg', 'mini', 'mitsubishi', 'nissan', 'opel', 'peugeot',
  'porsche', 'renault', 'rover', 'saab', 'seat', 'skoda', 'smart', 'ssangyong', 'subaru', 'suzuki',
  'tesla', 'toyota', 'volkswagen', 'volvo', 'zastava',
]);

/** A stable hue for a marque added to the catalogue after this table was written. */
function fallbackIdentity(name: string): BrandIdentity {
  let hash = 0;

  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) >>> 0;
  }

  const hue = hash % 360;
  const monogram = name
    .split(/[\s-]+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();

  return { colour: hslToHex(hue, 52, 38), monogram: monogram || '?' };
}

export function brandIdentity(make?: { name: string; slug?: string } | null): BrandIdentity {
  if (!make) {
    return { colour: '#5b6472', monogram: '', glyph: true };
  }

  const slug = make.slug ?? slugify(make.name);
  const identity = IDENTITIES[slug] ?? fallbackIdentity(make.name);

  return LOGOS.has(slug) ? { ...identity, logo: `/brands/${slug}.webp` } : identity;
}

const DIACRITICS: Record<string, string> = {
  đ: 'd',
};

/**
 * Latin-only slugging, matching how the seed data was generated.
 *
 * The decomposition pass is what makes "Citroën" and "Škoda" come out right; `đ` is the one letter
 * that has no decomposed form and so needs an explicit entry.
 */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[đ]/g, (match) => DIACRITICS[match] ?? match)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/* ------------------------------------------------------------------------------------------- */
/* Colour maths. Small enough to hand-roll, and it keeps a colour library out of the bundle.     */
/* ------------------------------------------------------------------------------------------- */

function parseHex(hex: string): [number, number, number] {
  const normalised = hex.replace('#', '');
  const full =
    normalised.length === 3
      ? normalised
          .split('')
          .map((character) => character + character)
          .join('')
      : normalised;

  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function toHex(r: number, g: number, b: number): string {
  const channel = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0');

  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function hslToHex(h: number, s: number, l: number): string {
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const secondary = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const match = lightness - chroma / 2;

  const [r, g, b] =
    h < 60
      ? [chroma, secondary, 0]
      : h < 120
        ? [secondary, chroma, 0]
        : h < 180
          ? [0, chroma, secondary]
          : h < 240
            ? [0, secondary, chroma]
            : h < 300
              ? [secondary, 0, chroma]
              : [chroma, 0, secondary];

  return toHex((r + match) * 255, (g + match) * 255, (b + match) * 255);
}

/** Straight-line blend in sRGB. Good enough for a two-stop badge gradient. */
export function mixHex(hex: string, towards: string, amount: number): string {
  const [r1, g1, b1] = parseHex(hex);
  const [r2, g2, b2] = parseHex(towards);
  const t = Math.max(0, Math.min(1, amount));

  return toHex(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t);
}

export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgb(${r} ${g} ${b} / ${alpha})`;
}

/**
 * Which ink stays legible on this colour.
 *
 * Half the marques in the catalogue are near-black and a handful (Renault, Opel, Lamborghini) are
 * bright yellow, so a fixed white monogram would be invisible on several badges.
 */
export function readableInk(hex: string): string {
  const [r, g, b] = parseHex(hex);

  const linear = (channel: number) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };

  const luminance = 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);

  return luminance > 0.42 ? '#141519' : '#ffffff';
}

/** The two-stop wash used by both the badge and the full-screen reveal. */
export function brandGradient(colour: string): string {
  return `linear-gradient(145deg, ${mixHex(colour, '#ffffff', 0.26)} 0%, ${colour} 48%, ${mixHex(
    colour,
    '#000000',
    0.3,
  )} 100%)`;
}
